import React, { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { Skeleton } from '@mui/material';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import MagicButton from 'components/MagicButton/MagicButton';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import {
  useGetMyBlocksQuery,
  useUnblockUserMutation,
} from 'store/services/block/blockApi';
import { BlockedUserEntry } from 'types/interfaces/block';
import './BlockedUsersTab.css';

const PAGE_LIMIT = 25;

const profileUrl = (entry: BlockedUserEntry): string =>
  entry.blockedUser.slug
    ? `/u/${entry.blockedUser.slug}`
    : `/profile/u/${entry.blockedUser.username}`;

const BlockedUsersTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError } = useGetMyBlocksQuery({
    page,
    limit: PAGE_LIMIT,
  });
  const [unblockUser] = useUnblockUserMutation();
  const [unblockingId, setUnblockingId] = useState<number | null>(null);

  const handleUnblock = async (entry: BlockedUserEntry) => {
    setUnblockingId(entry.blockedUser.id);
    try {
      await unblockUser({
        userId: entry.blockedUser.id,
        username: entry.blockedUser.username,
      }).unwrap();
      toast.success(`${entry.blockedUser.username} has been unblocked.`);
    } catch (error) {
      console.error('Failed to unblock user:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="blocked-users-tab">
      <h3 className="settings-section-title">Blocked Users</h3>
      <p className="blocked-users-tab__intro">
        Blocked users can&apos;t be matched with you or start trades with you.
        Unblocking restores normal trading immediately.
      </p>

      {isLoading && (
        <div className="blocked-users-tab__list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="blocked-users-tab__row">
              <Skeleton variant="circular" width={40} height={40} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <Skeleton variant="text" width={140} height={24} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            </div>
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <p className="blocked-users-tab__message">
          Failed to load your blocked users. Please try again later.
        </p>
      )}

      {!isLoading && !isError && data && data.blocks.length === 0 && page === 1 && (
        <p className="blocked-users-tab__message">You haven&apos;t blocked anyone yet.</p>
      )}

      {!isLoading && !isError && data && data.blocks.length > 0 && (
        <>
          <div className="blocked-users-tab__list">
            {data.blocks.map((entry) => (
              <div key={entry.id} className="blocked-users-tab__row">
                <Link to={profileUrl(entry)} className="blocked-users-tab__user">
                  <AvatarWithFlag
                    avatarUrl={entry.blockedUser.avatarUrl}
                    username={entry.blockedUser.username}
                    size={40}
                    isElite={entry.blockedUser.isElite}
                  />
                  <span className="blocked-users-tab__username">
                    {entry.blockedUser.username}
                    <EliteBadge isElite={!!entry.blockedUser.isElite} />
                  </span>
                </Link>
                <MagicButton
                  label={unblockingId === entry.blockedUser.id ? 'Unblocking...' : 'Unblock'}
                  color="grey"
                  onPress={() => handleUnblock(entry)}
                  isLoading={unblockingId === entry.blockedUser.id}
                  isDisabled={unblockingId !== null}
                  fullWidth={false}
                />
              </div>
            ))}
          </div>

          {(data.hasMore || page > 1) && (
            <div className="blocked-users-tab__pagination">
              <MagicButton
                label="Previous"
                color="grey"
                onPress={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page <= 1 || isFetching}
                fullWidth={false}
              />
              <MagicButton
                label="Next"
                color="grey"
                onPress={() => setPage((p) => p + 1)}
                isDisabled={!data.hasMore || isFetching}
                fullWidth={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlockedUsersTab;
